/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import {
    IBuyboxAddToCartViewProps,
    IBuyboxAddToOrderTemplateViewProps,
    IBuyboxAddToWishlistViewProps,
    IBuyboxCallbacks,
    IBuyboxData,
    IBuyboxKeyInPriceViewProps,
    IBuyboxProductConfigureDropdownViewProps,
    IBuyboxProductConfigureViewProps,
    IBuyboxProductQuantityViewProps,
    IBuyboxProps,
    IBuyboxShopSimilarLookViewProps,
    IBuyboxState
} from '@msdyn365-commerce-modules/buybox';
import { IBuyboxViewProps } from './../../../modules/custom-buybox/custom-buybox';
import { IncrementalQuantity, ITelemetryContent, Module, Node } from '@msdyn365-commerce-modules/utilities';
import { SimpleProduct } from '@msdyn365-commerce/retail-proxy';
import * as React from 'react';

import {
    IBuyboxProps as IBuyboxExtentionProps,
    IBuyboxResources as IBuyboxExtentionResources
} from '../definition-extensions/buybox.ext.props.autogenerated';
import { ICustomBuyboxResources } from '../../../modules/custom-buybox';

const checkUsedEquipmentFlag = (product: SimpleProduct | undefined): boolean => {
    let usedEquipmentFlag = false;


    if (product?.ExtensionProperties) {
        product.ExtensionProperties.forEach(property => {
            if (property.Key === 'UsedEquipment' && property.Value?.StringValue === '1') {
                usedEquipmentFlag = true;
            }
        });
    }

    return usedEquipmentFlag;
};

const checkWebsitePricingAllowed = (product: SimpleProduct | undefined): boolean => {
    let checkUsedPricingFlag = false;

    if (product?.ExtensionProperties) {
        product.ExtensionProperties.forEach(property => {
            if (property.Key === 'WebsitePricingAllowed' && property.Value?.StringValue === '1') {
                checkUsedPricingFlag = true;
            }
        });
    }
    return checkUsedPricingFlag;
};


const BuyboxView: React.FC<IBuyboxViewProps & IBuyboxExtentionProps<IBuyboxData>> = props => {
    const {
        ModuleProps,
        MediaGalleryContainerProps,
        ProductInfoContainerProps,
        addToCart,
        addToOrderTemplate,
        addToWishlist,
        telemetryContent,
        configure,
        description,
        quantity,
        title,
        rating,
        callbacks,
        state,
        resources,
        inventoryLabel,
        shopSimilarLook,
        keyInPrice,
        quantityLimitsMessages,
        min,
        max,
        applyDefaultOrderSettings,
        shopSimilarDescription,
        unitOfMeasure,
        productTypes
    } = props;

    const usedEquipmentFlag = checkUsedEquipmentFlag(props.data.product?.result);
    const WebsitePricingAllowed = checkWebsitePricingAllowed(props.data.product?.result);
    
    React.useEffect(() => {
        const thumbnailContainer = document.querySelector('.ms-media-gallery__thumbnails-container') as HTMLElement;
        if (thumbnailContainer) {
            if (usedEquipmentFlag) {
                thumbnailContainer.style.display = 'none';
            } else {
                thumbnailContainer.style.display = '';
            }
        }
    }, [usedEquipmentFlag]);

    return (
        <Module {...ModuleProps}>
            <Node {...MediaGalleryContainerProps}>{props.mediaGallery}
                {usedEquipmentFlag && props.resources.pictureRepresentativeOnlyText}
            </Node>
            <Node {...ProductInfoContainerProps}>
                {title}
                {!usedEquipmentFlag && (
                    WebsitePricingAllowed ? (
                        _renderCustomPrice(props.data.product.result, props.context.request.channel?.Currency, props.resources)
                    ) : (
                        <span>{props.resources.contactForPricingText}</span>
                    )
                )}

                {unitOfMeasure}
                {props.bulkPurchaseLink}
                {description}
                {_renderTextBlock(props.slots.textBlocks)}
                {rating}
                {configure && _renderConfigure(configure)}
                {keyInPrice && _renderKeyInPrice(keyInPrice)}
                {!usedEquipmentFlag && quantity && !props.state.isNoOptions &&
                    _renderQuantity(
                        quantity,
                        callbacks,
                        props,
                        state,
                        resources,
                        quantityLimitsMessages,
                        min,
                        max,
                        applyDefaultOrderSettings,
                        telemetryContent
                    )}
                {inventoryLabel}
                {productTypes}
                {/* {addToCart && !props.state.isNoOptions && _renderAddToCart(addToCart)} */}
                {!props.state.isNoOptions && _renderCartAndActions(addToCart, addToOrderTemplate, addToWishlist)}
                {_renderSocialShare(props.slots && props.slots.socialShare)}
                {shopSimilarLook && _renderShopSimilarItem(shopSimilarLook)}
                {shopSimilarDescription && _renderShopSimilarItem(shopSimilarDescription)}
                {props.rdaCustomHtml}
            </Node>
        </Module>
    );
};

const _renderCustomPrice = (product: SimpleProduct | undefined, _currencyCode: string | undefined, _props: ICustomBuyboxResources): JSX.Element => {
    let _Amount4W: string | undefined;
    let _Amount1D: string | undefined;
    let _Amount7D: string | undefined;

    if (product !== undefined) {
        product.ExtensionProperties?.map(function(property) {
            if (property.Key === 'Amount4W' && property.Value?.StringValue) {
                _Amount4W = property.Value?.StringValue;
            }
            if (property.Key === 'Amount1D' && property.Value?.StringValue) {
                _Amount1D = property.Value?.StringValue;
            }
            if (property.Key === 'Amount7D' && property.Value?.StringValue) {
                _Amount7D = property.Value?.StringValue;
            }
        });
    }

    return (
        <>
            {
                <>
                    <div className='price-box'>
                        {_Amount1D && (
                            <div>
                                <h4>
                                    ${_Amount1D}
                                    <span>/{_currencyCode}</span>
                                </h4>
                                <p>{_props.labelOneDay}</p>
                            </div>
                        )}
                        {_Amount7D && (
                            <div>
                                <h4>
                                    ${_Amount7D}
                                    <span>/{_currencyCode}</span>
                                </h4>
                                <p>{_props.labelOneWeek}</p>
                            </div>
                        )}
                        {_Amount4W && (
                            <div>
                                <h4>
                                    ${_Amount4W}
                                    <span>/{_currencyCode}</span>
                                </h4>
                                <p>{_props.labelFourWeek}</p>
                            </div>
                        )}
                    </div>
                    <div className='price-one-week-min-rental'>{_props.labelMinRental}</div>
                </>
            }
        </>
    );
};

const _renderTextBlock = (textBlocks: React.ReactNode[]): JSX.Element | undefined => {
    if (!textBlocks || textBlocks.length === 0) {
        return undefined;
    }

    return <>{textBlocks[0]}</>;
};

const _renderSocialShare = (socialShare: React.ReactNode[]): JSX.Element | undefined => {
    if (!socialShare || socialShare.length === 0) {
        return undefined;
    }

    return <>{socialShare[0]}</>;
};

const _renderCartAndActions = (
    addToCart?: IBuyboxAddToCartViewProps,
    addToOrderTemplate?: IBuyboxAddToOrderTemplateViewProps,
    addToWishlist?: IBuyboxAddToWishlistViewProps
): JSX.Element | undefined => {
    if (!addToCart && !addToWishlist) {
        return undefined;
    }

    return (
        <div className='product-add-to-cart'>
            {addToCart && addToCart.errorBlock}
            {addToOrderTemplate && addToOrderTemplate.errorBlock}
            {addToWishlist && addToWishlist.errorBlock}
            <div className='buttons'>
                {addToCart && addToCart.button}
                {addToOrderTemplate && addToOrderTemplate.button}
                {addToWishlist && addToWishlist.button}
            </div>
        </div>
    );
};

const _renderConfigure = (configure: IBuyboxProductConfigureViewProps): JSX.Element => {
    const { ContainerProps, dropdowns } = configure;

    return <Node {...ContainerProps}>{dropdowns.map(_renderConfigureDropdown)}</Node>;
};

const _renderConfigureDropdown = (dropdown: IBuyboxProductConfigureDropdownViewProps): JSX.Element => {
    const { ContainerProps, LabelContainerProps, heading, errors, select } = dropdown;

    return (
        <Node {...ContainerProps}>
            <Node {...LabelContainerProps}>
                {heading}
                {errors}
            </Node>
            {select}
        </Node>
    );
};

// const _renderFindInStore = (findInStore: IBuyboxFindInStoreViewProps): JSX.Element => {
//     const { ContainerProps, storeSelector, heading, description, errors, button, modal, productPickupOptionList } = findInStore;

//     return (
//         <Node {...ContainerProps}>
//             {storeSelector}
//             {heading}
//             {productPickupOptionList}
//             {description}
//             {errors}
//             {button}
//             {modal}
//         </Node>
//     );
// };

/**
 * On Change function.
 * @param callbacks -Callbacks.
 * @returns Update quantity.
 */
const onChangeHandler = (callbacks: IBuyboxCallbacks) => (newValue: number): boolean => {
    if (callbacks.updateQuantity) {
        return callbacks.updateQuantity(newValue);
    }
    return true;
};

const _renderQuantity = (
    quantityComponent: IBuyboxProductQuantityViewProps,
    callbacks: IBuyboxCallbacks,
    props: IBuyboxProps<IBuyboxData>,
    state: IBuyboxState,
    extentionResources: IBuyboxExtentionResources,
    quantityLimitsMessages: React.ReactNode,
    min: number | undefined,
    max: number | undefined,
    applyDefaultOrderSettings?: boolean,
    telemetryContent?: ITelemetryContent
): JSX.Element => {
    const { ContainerProps, LabelContainerProps, heading, errors } = quantityComponent;

    const { resources } = props;

    const { quantity } = state;

    return (
        <Node {...ContainerProps}>
            <Node {...LabelContainerProps}>
                {heading}
                {errors}
            </Node>

            <IncrementalQuantity
                id='ms-buybox__product-quantity-input'
                min={min}
                max={max}
                applyDefaultOrderSettings={applyDefaultOrderSettings}
                currentCount={quantity}
                onChange={onChangeHandler(callbacks)}
                inputQuantityAriaLabel={resources.inputQuantityAriaLabel}
                decrementButtonAriaLabel={extentionResources.decrementButtonAriaLabel}
                incrementButtonAriaLabel={extentionResources.incrementButtonAriaLabel}
                minQuantityText={extentionResources.minQuantityText}
                maxQuantityText={extentionResources.maxQuantityText}
                telemetryContent={telemetryContent}
            />
            {quantityLimitsMessages}
            <div className='rda-pdp-availability-text'>{extentionResources.productSubjectToAvailability}</div>
        </Node>
    );
};

const _renderKeyInPrice = (keyInPrice: IBuyboxKeyInPriceViewProps): JSX.Element => {
    const { ContainerProps, LabelContainerProps, heading, input } = keyInPrice;

    return (
        <Node {...ContainerProps}>
            <Node {...LabelContainerProps}>{heading}</Node>
            {input}
        </Node>
    );
};

const _renderShopSimilarItem = (shopSimilarItem: IBuyboxShopSimilarLookViewProps): JSX.Element => {
    const { ContainerProps, errors, input } = shopSimilarItem;

    return (
        <Node {...ContainerProps}>
            {errors}
            {input}
        </Node>
    );
};

export default BuyboxView;
